import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import LoadingSpinner from '@/components/ui/loading-spinner';
import { Target, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import KeywordRow from './KeywordRow';




interface KeywordsTableProps {
  keywords: any[];
  isLoadingKeywords: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  selectedKeywords: Set<string>;
  onSelectAll: (checked: boolean) => void;
  onSelectKeyword: (keywordId: string, checked: boolean) => void;
  setCurrentPage: (page: number) => void;
  isAnalyzing?: boolean;
  analyzingKeywordIds?: Set<string>;
}


const KeywordsTable: React.FC<KeywordsTableProps> = React.memo(({
  keywords,
  isLoadingKeywords,
  totalCount,
  currentPage,
  totalPages,
  selectedKeywords,
  onSelectAll,
  onSelectKeyword,
  setCurrentPage,
  isAnalyzing = false,
  analyzingKeywordIds = new Set(),
}) => {
  const isAllSelected = keywords.length > 0 && selectedKeywords.size === keywords.length;
  const isIndeterminate = selectedKeywords.size > 0 && selectedKeywords.size < keywords.length;

  if (isLoadingKeywords) {
    return (
      <div className="min-h-[400px] flex items-center justify-center space-y-4">
        <LoadingSpinner text="Loading keywords..." />
      </div>
    );
  }

  if (keywords.length === 0) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center  px-0">
        <Target className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Keywords Found</h3>
        <p className="text-muted-foreground">
          No keywords were discovered for this research query.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 min-h-[400px]">
      {/* Table Header */}
      <div className="flex items-center  gap-4 p-3 bg-muted/50 rounded-lg font-medium text-sm min-w-fit text-[oklch(70.4%_0.04_256.788)]">
        <div className="w-4 flex items-center justify-center">
          <Checkbox
            checked={isAllSelected}
            ref={(el) => {
              if (el) {
                const inputEl = el as HTMLInputElement;
                inputEl.indeterminate = isIndeterminate;
              }
            }}
            onCheckedChange={onSelectAll}
          />
        </div>
        <div className="w-[18%] min-w-0">Keyword</div>
        <div className="w-[12%] min-w-0">Volume</div>
        <div className="w-[10%] min-w-0">KD</div>
        <div className="w-[15%] min-w-0">CPC</div>
        <div className="w-[20%] min-w-0">Category</div>
        <div className="w-[25%] min-w-0">Trend</div>
        <div className="w-[10%] min-w-0">Actions</div>
      </div>

      {/* Keywords Table */}
      <div className="divide-y divide-white/5">
        {keywords.map((keyword: any) => {
          const hasDAScore = keyword.da_score !== null;
          const keywordId = keyword.id.toString();
          const isSelected = selectedKeywords.has(keywordId);
          const isBeingAnalyzed = analyzingKeywordIds.has(keywordId);
          
          return (
            <KeywordRow
              key={keyword.id}
              keyword={keyword}
              isSelected={isSelected}
              hasDAScore={hasDAScore}
              isBeingAnalyzed={isBeingAnalyzed}
              onSelectKeyword={onSelectKeyword}
            />
          );
      })}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} keywords
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

KeywordsTable.displayName = 'KeywordsTable';

export default KeywordsTable; 